/* Pure wall geometry (DOM-free; parameterized by wall lists, not app state). */
import type { Doc, Pt, Wall } from './types.ts';
import { clamp } from './utils.ts';

/** Endpoints closer than this are considered welded into one corner. */
export const WELD_TOL = 0.75;

export interface WallVec { ux: number; uy: number; nx: number; ny: number; len: number }

export function wallVec(w: Wall): WallVec {
  const dx = w.x2 - w.x1, dy = w.y2 - w.y1, len = Math.hypot(dx, dy) || 1e-9;
  const ux = dx / len, uy = dy / len;
  return { ux, uy, nx: -uy, ny: ux, len };
}

export const wallPt = (w: Wall, t: number): Pt => {
  const v = wallVec(w);
  return { x: w.x1 + v.ux * t, y: w.y1 + v.uy * t };
};

export function distToSeg(px: number, py: number, x1: number, y1: number, x2: number, y2: number) {
  const dx = x2 - x1, dy = y2 - y1, l2 = dx * dx + dy * dy;
  const t = l2 ? clamp(((px - x1) * dx + (py - y1) * dy) / l2, 0, 1) : 0;
  const qx = x1 + t * dx, qy = y1 + t * dy;
  return { d: Math.hypot(px - qx, py - qy), t: t * Math.sqrt(l2) };
}

export function segIntersectsRect(x1: number, y1: number, x2: number, y2: number,
                                  rx1: number, ry1: number, rx2: number, ry2: number) {
  // Liang-Barsky
  let t0 = 0, t1 = 1; const dx = x2 - x1, dy = y2 - y1;
  const p = [-dx, dx, -dy, dy], q = [x1 - rx1, rx2 - x1, y1 - ry1, ry2 - y1];
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) { if (q[i] < 0) return false; }
    else {
      const r = q[i] / p[i];
      if (p[i] < 0) { if (r > t1) return false; if (r > t0) t0 = r; }
      else { if (r < t0) return false; if (r < t1) t1 = r; }
    }
  }
  return true;
}

/** Walls having an endpoint at pt (within weld tolerance). */
export function wallsAt(walls: Wall[], pt: Pt, excludeIds?: Set<string>): Wall[] {
  const out: Wall[] = [];
  for (const w of walls) {
    if (excludeIds && excludeIds.has(w.id)) continue;
    if (Math.hypot(w.x1 - pt.x, w.y1 - pt.y) < WELD_TOL || Math.hypot(w.x2 - pt.x, w.y2 - pt.y) < WELD_TOL) out.push(w);
  }
  return out;
}

/**
 * Interior clear span: centerline length minus what the walls joined at each
 * end eat with their thickness (th/2 ÷ |sin| of the joint angle).
 */
export function wallClear(walls: Wall[], wl: Wall): number {
  const v = wallVec(wl);
  let eat = 0;
  for (const [px, py] of [[wl.x1, wl.y1], [wl.x2, wl.y2]] as const) {
    let e = 0;
    for (const w of walls) {
      if (w === wl) continue;
      if (Math.hypot(w.x1 - px, w.y1 - py) >= WELD_TOL && Math.hypot(w.x2 - px, w.y2 - py) >= WELD_TOL) continue;
      const u2 = wallVec(w);
      const cross = Math.abs(v.ux * u2.uy - v.uy * u2.ux);
      if (cross > .2) e = Math.max(e, w.th / (2 * cross));
    }
    eat += e;
  }
  return v.len - eat;
}

/** Signed perpendicular distance from wall A's centerline to wall B's midpoint. */
export function constraintDist(A: Wall, B: Wall): number {
  const vA = wallVec(A);
  const midB = { x: (B.x1 + B.x2) / 2, y: (B.y1 + B.y2) / 2 };
  return (midB.x - A.x1) * vA.nx + (midB.y - A.y1) * vA.ny;
}

/** Angle of wall A → wall B directions folded into [0, 180) degrees. */
export function wallAngleDeg(A: Wall, B: Wall): number {
  const a = Math.atan2(A.y2 - A.y1, A.x2 - A.x1);
  const b = Math.atan2(B.y2 - B.y1, B.x2 - B.x1);
  let d = (b - a) * 180 / Math.PI;
  d = ((d % 180) + 180) % 180;
  return d;
}

/** Which side of the wall centerline p falls on (sign of the left-normal component). */
export function distSide(wl: Wall, p: Pt): number {
  const v = wallVec(wl);
  return (p.x - wl.x1) * v.nx + (p.y - wl.y1) * v.ny;
}

export function nearestWall(walls: Wall[], p: Pt, tolWorld: number) {
  let best: { wall: Wall; t: number; d: number } | null = null;
  for (const w of walls) {
    const r = distToSeg(p.x, p.y, w.x1, w.y1, w.x2, w.y2);
    if (r.d <= tolWorld + w.th / 2 && (!best || r.d < best.d)) best = { wall: w, t: r.t, d: r.d };
  }
  return best;
}

export function docBBox(doc: Doc) {
  let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
  const add = (x: number, y: number) => { x1 = Math.min(x1, x); y1 = Math.min(y1, y); x2 = Math.max(x2, x); y2 = Math.max(y2, y); };
  for (const w of doc.walls) { add(w.x1, w.y1); add(w.x2, w.y2); }
  for (const f of doc.fixtures) { const r = Math.hypot(f.w, f.h) / 2; add(f.x - r, f.y - r); add(f.x + r, f.y + r); }
  for (const l of doc.labels) { add(l.x - 20, l.y - 10); add(l.x + 20, l.y + 10); }
  if (x1 === Infinity) return null;
  return { x1, y1, x2, y2 };
}
